import { useEffect, useMemo, useRef, useState } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import {
  ActionIcon, Box, Button, createStyles, Drawer, Loader, MultiSelect, SegmentedControl,
  Select, Text, TextInput,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconCalendar, IconChevronDown, IconChevronLeft, IconChevronRight,
  IconCheck, IconDownload, IconFileText, IconSettings, IconUsers, IconX,
} from "@tabler/icons-react";
import { useSelector } from "react-redux";
import { decrypt, showError } from "../../utils/index";
import { fetchServiceRequestsForReport } from "../../services/serviceRequests";
import { clientRefName, displayRef, refId, SHIFTS } from "../ServiceRequestFields";
import { STATUS_LABELS } from "../ServiceRequestsTable";

const ALL_FACILITIES = "All facilities";
const ANY_SHIFT = "Any shift";

const c = {
  bg: "#F5F5F5",
  surface: "#FFFFFF",
  surfaceSoft: "#FAFAFA",
  surfaceAlt: "#F4F4F5",
  line: "#E5E5E5",
  lineSoft: "#F3F4F6",
  ink: "#171717",
  ink2: "#262626",
  muted: "#667085",
  muted2: "#9CA3AF",
  accent: "#FFBA45",
  accentSoft: "#FFF8EB",
  accentHover: "#EEAA33",
  accentRing: "#F4D8A2",
  danger: "#DC2626",
};

const FONT = "'Inter', -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/* ---- calendar helpers (display-only; no date-math changes) ---- */
const CAL_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const CAL_MABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const pad2 = (n) => String(n).padStart(2, "0");
const dnum = (d) => d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
const dayLabel = (d) => `${CAL_MABBR[d.getMonth()]} ${pad2(d.getDate())}, ${d.getFullYear()}`;
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };

const buildPresets = () => {
  const today = new Date();
  const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
  const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const lastMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  return [
    { k: "7", label: "Last 7 days", range: () => [addDays(today, -6), today] },
    { k: "30", label: "Last 30 days", range: () => [addDays(today, -29), today] },
    { k: "tm", label: "This month", range: () => [startOfMonth(today), today] },
    { k: "lm", label: "Last month", range: () => [startOfMonth(lastMonthDate), endOfMonth(lastMonthDate)] },
    { k: "ytd", label: "Year to date", range: () => [new Date(today.getFullYear(), 0, 1), today] },
  ];
};

const useStyles = createStyles(() => ({
  drawerContent: {
    borderRadius: "16px 0 0 16px",
    boxShadow: "0 24px 60px rgba(16,24,40,0.22)",
    border: `1px solid ${c.line}`,
    borderRight: 0,
    "@media (max-width: 768px)": { borderRadius: 0, border: 0 },
    "@media print": { border: "none", boxShadow: "none", background: "#fff" },
  },
  root: {
    // Explicit viewport height, not a `100%` that depends on every
    // ancestor in the chain also having a resolved height — that's what
    // let the preview's tall PDF-page stack grow the whole panel instead of
    // scrolling inside it, taking the footer down the page with it.
    display: "flex", flexDirection: "column", height: "100vh", minHeight: 0, background: c.bg,
    fontFamily: FONT,
  },

  header: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16,
    padding: "22px 28px 18px", borderBottom: `1px solid ${c.lineSoft}`, flexShrink: 0, background: c.surface,
    "@media (max-width: 768px)": { padding: "14px 16px 12px", gap: 10 },
  },
  headerMin: { minWidth: 0 },
  title: { fontSize: 20, fontWeight: 700, color: c.ink, letterSpacing: "-0.015em", fontFamily: FONT },
  headerSub: { margin: "4px 0 0", fontSize: 13, color: c.muted, lineHeight: 1.45, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", fontFamily: FONT },
  closeBtn: {
    width: 30, height: 30, borderRadius: 999, background: c.surfaceAlt, color: c.ink2, flexShrink: 0,
    "&:hover": { background: "#E4E4E7" },
  },

  body: {
    flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 14,
    "@media (max-width: 768px)": { padding: 12, gap: 12 },
  },
  printHide: { "@media print": { display: "none !important" } },
  printShell: {
    width: "100%",
    "@media print": {
      display: "block",
      padding: 0,
      background: "#fff",
      boxShadow: "none",
      gap: 0,
      overflow: "visible",
    },
  },

  card: {
    background: c.surface, border: `1px solid ${c.line}`, borderRadius: 12, boxShadow: "0 1px 2px rgba(16,24,40,.04)", padding: 16,
    "@media (max-width: 768px)": { padding: 13 },
  },
  cardHead: {
    display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: c.ink, marginBottom: 14,
    fontFamily: FONT,
    "& svg": { color: c.muted },
  },
  cardHeadRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 14 },

  grid: { display: "grid", gridTemplateColumns: "repeat(12, 1fr)", columnGap: 16, rowGap: 16 },
  span12: { gridColumn: "span 12" },
  // Staff/Shift and Group by/Sort order pair up on a wide drawer; at phone
  // width a half-column can't hold a multi-select's chip tray.
  span6: { gridColumn: "span 6", "@media (max-width: 640px)": { gridColumn: "span 12" } },

  /* label row with a right-aligned "Select all" link (mockup .rep-lblrow / .rep-linklike) */
  lblRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 6 },
  msLabel: {
    fontSize: 11.5, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: c.muted2,
    fontFamily: FONT,
  },
  req: { color: c.danger },
  linkBtn: {
    border: 0, background: "none", color: c.accentHover, fontWeight: 600, fontSize: 12, cursor: "pointer",
    fontFamily: FONT, padding: 0, whiteSpace: "nowrap",
    "&:hover": { textDecoration: "underline" },
  },
  /* hint sits BELOW the control in the mockup (.rep-hint), not above it */
  hint: { fontSize: 11.5, color: c.muted2, marginTop: 6, fontFamily: FONT },

  advBtn: {
    alignSelf: "flex-end", height: "auto", padding: "9px 14px", borderRadius: 8, border: `1px solid ${c.line}`,
    background: c.surfaceAlt, color: c.ink2, fontSize: 13, fontWeight: 600, fontFamily: FONT,
    "&:hover": { background: "#ECECEF" },
  },
  advCaretUp: { transform: "rotate(180deg)", transition: "transform .15s" },
  advCaretDown: { transform: "rotate(0deg)", transition: "transform .15s" },
  advCloseBtn: {
    width: 28, height: 28, borderRadius: 8, color: c.muted2,
    "&:hover": { background: c.surfaceAlt, color: c.ink },
  },
  segLabel: { fontSize: 11.5, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: c.muted2, marginBottom: 6, fontFamily: FONT },
  optTag: { color: c.muted2, fontWeight: 500, textTransform: "none", letterSpacing: 0 },

  /* date-range trigger + popover calendar (mirrors mockup .rep-daterange / .rep-cal) */
  dateRangeWrap: { position: "relative" },
  dateRangeLabel: { fontSize: 11.5, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: c.muted2, marginBottom: 6, fontFamily: FONT },
  dateRangeBtn: {
    height: 40, width: "100%", border: `1px solid ${c.line}`, borderRadius: 8, display: "flex", alignItems: "center",
    justifyContent: "space-between", padding: "0 12px", fontSize: 13.5, color: c.ink, background: c.surface,
    cursor: "pointer", fontFamily: FONT,
    "& svg": { color: c.muted2 },
  },
  dateRangeBtnOpen: { borderColor: c.accentRing, boxShadow: `0 0 0 3px ${c.accentSoft}` },
  cal: {
    position: "absolute", top: "calc(100% + 8px)", left: 0, zIndex: 40, background: c.surface,
    border: `1px solid ${c.line}`, borderRadius: 12, boxShadow: "0 12px 30px rgba(16,24,40,.14)", padding: 12,
    width: "min(420px, 90vw)", fontFamily: FONT,
    // Anchored to both edges so it can't hang off the right of a phone screen.
    "@media (max-width: 560px)": { left: 0, right: 0, width: "auto", padding: 10 },
  },
  // 118px of presets beside a 7-column month grid leaves the grid ~180px wide on
  // a phone — under 26px a day. The presets become a scrollable row on top
  // instead, handing the whole width to the calendar.
  calTop: { display: "flex", gap: 12, "@media (max-width: 560px)": { flexDirection: "column", gap: 10 } },
  presets: {
    display: "flex", flexDirection: "column", gap: 2, width: 118, flexShrink: 0, borderRight: `1px solid ${c.lineSoft}`, paddingRight: 10,
    "@media (max-width: 560px)": {
      flexDirection: "row", width: "auto", overflowX: "auto", paddingRight: 0, paddingBottom: 8,
      borderRight: 0, borderBottom: `1px solid ${c.lineSoft}`,
    },
  },
  preset: {
    textAlign: "left", border: "none", background: "none", fontFamily: FONT, fontSize: 12.5, color: c.ink2,
    padding: "7px 8px", borderRadius: 7, cursor: "pointer", whiteSpace: "nowrap",
    "&:hover": { background: c.surfaceSoft },
    "@media (max-width: 560px)": { flex: "none", border: `1px solid ${c.line}` },
  },
  presetActive: { background: c.accentSoft, color: "#B45309", fontWeight: 600 },
  calMain: { flex: 1, minWidth: 0 },
  calHead: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 },
  calTitle: { fontSize: 13, fontWeight: 700, color: c.ink, fontFamily: FONT },
  calNav: {
    border: `1px solid ${c.line}`, background: c.surface, width: 28, height: 28, borderRadius: 7,
    display: "grid", placeItems: "center", cursor: "pointer", color: c.ink2,
    "&:hover": { background: c.surfaceSoft },
  },
  calGrid: { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 },
  calDow: { fontSize: 9.5, fontWeight: 700, color: c.muted2, textAlign: "center", padding: "3px 0", textTransform: "uppercase", letterSpacing: "0.03em" },
  calDay: {
    height: 30, border: "none", background: "none", fontFamily: FONT, fontSize: 12, color: c.ink,
    borderRadius: 7, cursor: "pointer", display: "grid", placeItems: "center",
    "&:hover": { background: c.surfaceSoft },
  },
  calDayMuted: { color: c.muted2, opacity: 0.45 },
  calDayInRange: { background: c.accentSoft, borderRadius: 0 },
  calDayStart: { background: c.accent, color: c.ink, fontWeight: 700, borderRadius: "7px 0 0 7px" },
  calDayEnd: { background: c.accent, color: c.ink, fontWeight: 700, borderRadius: "0 7px 7px 0" },
  calDayStartEnd: { background: c.accent, color: c.ink, fontWeight: 700, borderRadius: 7 },
  calFoot: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 10, paddingTop: 10,
    borderTop: `1px solid ${c.lineSoft}`,
    "@media (max-width: 400px)": { alignItems: "stretch", flexDirection: "column" },
  },
  calRangeLbl: { fontSize: 12, color: c.muted, fontFamily: FONT, "& strong": { color: c.ink, fontWeight: 600 } },
  calDoneBtn: {
    height: 30, borderRadius: 7, border: `1px solid ${c.line}`, background: c.surface, color: c.ink,
    fontSize: 12.5, fontWeight: 600, fontFamily: FONT, padding: "0 12px",
    "&:hover": { background: c.surfaceSoft },
  },

  footer: {
    display: "flex", justifyContent: "flex-end", gap: 12, padding: "16px 28px",
    borderTop: `1px solid ${c.lineSoft}`, background: c.surface, flexShrink: 0,
    "& > button": { flex: 1, justifyContent: "center" },
    "@media (max-width: 768px)": { padding: "12px 14px", gap: 10 },
  },
  cancelBtn: {
    height: 34, borderRadius: 8, border: `1px solid ${c.line}`, background: c.surface, color: c.ink,
    fontSize: 13, fontWeight: 600, fontFamily: FONT,
    "&:hover": { background: c.surfaceSoft, borderColor: "#D4D4D8" },
  },
  primaryBtn: {
    height: 40, borderRadius: 8, background: c.accent, border: 0, color: c.ink, fontSize: 13.5, fontWeight: 700,
    fontFamily: FONT,
    "&:hover": { background: `${c.accent} !important`, opacity: 0.85 },
    "&:disabled": { opacity: 0.45, background: c.accent, color: c.ink },
  },

  /* ---- preview stage (drawer chrome around the simulated PDF; not the PDF's own colors) ---- */
  drawerCentered: { borderRadius: 16 },
  previewStage: {
    background: "#ECECEE", display: "flex", flexDirection: "column", alignItems: "center", gap: 22,
    overflowY: "auto", minHeight: 0, padding: "28px 24px",
    "@media (max-width: 768px)": { gap: 14, padding: "14px 10px" },
  },
  // On the "ready" step the pages themselves aren't shown — they stay
  // mounted off-screen purely so "Download again" can re-capture them
  // without navigating back through the preview step first.
  previewOffscreen: { position: "fixed", top: 0, left: -10000, width: 600, pointerEvents: "none" },

  /* pdf page — literal near-white/near-black regardless of app theme */
  pdfpage: {
    width: "100%", maxWidth: 600, flexShrink: 0, margin: "0 auto", background: "#fff",
    border: "1px solid #E4E4E7", boxShadow: "0 6px 30px rgba(16,24,40,.14)", padding: "40px 44px", color: "#141414",
    fontFamily: FONT,
    // Preview-only breathing room — the capture forces the desktop width back on
    // (see downloadReportPdf), so this never reaches the PDF.
    "@media (max-width: 768px)": { padding: "22px 18px" },
    "@media print": {
      maxWidth: "100%",
      width: "100%",
      border: "none",
      boxShadow: "none",
      padding: "24px 30px",
      pageBreakAfter: "always",
    },
  },
  repPage: { display: "flex", flexDirection: "column", minHeight: 776 },
  pdfLh: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, borderBottom: "2px solid #141414", paddingBottom: 14, marginBottom: 20 },
  pdfBrand: { display: "flex", alignItems: "center", gap: 12 },
  pdfLogo: { width: 48, height: 48, borderRadius: 10, background: "transparent", display: "grid", placeItems: "center", overflow: "hidden", flex: "none" },
  pdfLogoImg: { width: "100%", height: "100%", objectFit: "contain", display: "block" },
  pdfOrg: { fontSize: 16, fontWeight: 800, letterSpacing: "-0.01em", color: "#141414" },
  pdfRt: { textAlign: "right", fontSize: 11, color: "#667085", lineHeight: 1.5 },
  pdfTitle: { fontSize: 21, fontWeight: 700, margin: 0, color: "#141414" },
  pdfSub2: { fontSize: 12.5, color: "#667085", marginTop: 4 },
  pdfFoot: { marginTop: "auto", paddingTop: 12, borderTop: "1px solid #EAEAEA", display: "flex", justifyContent: "space-between", fontSize: 10.5, color: "#98A2B3" },

  rcMid: { flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", padding: "30px 0" },
  rcGrid: { border: "1px solid #EAEAEA", borderRadius: 10, overflow: "hidden" },
  rcRow: { display: "flex", borderBottom: "1px solid #E4E4E7", "&:last-child": { borderBottom: "none" } },
  rcK: { width: 170, flex: "none", padding: "11px 16px", fontSize: 11.5, fontWeight: 700, color: "#171717", background: "#F7F7F8", borderRight: "1px solid #E4E4E7" },
  rcV: { flex: 1, padding: "11px 16px", fontSize: 11.5, color: "#171717", lineHeight: 1.5 },
  // Marks a Group by boundary — each entry still gets its own page, this
  // just calls out which cluster the page belongs to.
  groupLabel: { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#98A2B3", marginTop: 18 },

  reList: { marginTop: 18 },
  reEntry: { border: "1px solid #E4E4E7", borderRadius: 12, background: "#fff", overflow: "hidden", marginBottom: 16, boxShadow: "0 1px 2px rgba(16,24,40,.05)" },
  reHead: { display: "flex", alignItems: "flex-start", gap: 11, padding: "13px 16px", borderBottom: "1px solid #E4E4E7", flexWrap: "wrap" },
  reWho: { minWidth: 0, flex: 1 },
  reK: { fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#98A2B3", marginBottom: 3 },
  reClient: { fontSize: 14, fontWeight: 700, color: "#171717", lineHeight: 1.45 },
  reChip: { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", padding: "3px 9px", borderRadius: 6, background: "#F2F2F2", border: "1px solid #E0E0E0", color: "#475467", whiteSpace: "nowrap" },
  reWhen: { marginLeft: "auto", textAlign: "right" },
  reWhenD: { fontSize: 12, fontWeight: 700, color: "#171717" },
  reWhenS: { fontSize: 11, color: "#667085", marginTop: 1 },
  reBody: { padding: "14px 16px 15px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 26px" },
  reF: { minWidth: 0 },
  reFFull: { gridColumn: "1/-1" },
  reFK: { fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#98A2B3", marginBottom: 3 },
  reFV: { fontSize: 12, color: "#171717", lineHeight: 1.5 },
  reFVBoxed: { background: "#F7F7F8", border: "1px solid #E4E4E7", borderRadius: 8, padding: "9px 12px" },
  reFoot: { display: "flex", alignItems: "flex-end", justifyContent: "flex-end", gap: "14px 22px", padding: "10px 16px 12px", borderTop: "1px solid #E4E4E7", background: "#F7F7F8", flexWrap: "wrap" },
  reSiglbl: { fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#98A2B3", paddingBottom: 5, marginRight: "auto" },
  reSigblk: { textAlign: "center", minWidth: 110 },
  reSig: {
    fontFamily: "'Segoe Script','Snell Roundhand','Brush Script MT',cursive", fontSize: 17, color: "#171717",
    padding: "0 8px 3px", minHeight: 22, display: "flex", alignItems: "center", justifyContent: "center",
  },
  reSigImg: { maxWidth: 100, maxHeight: 32, objectFit: "contain" },
  reSigname: { fontSize: 10.5, color: "#667085", borderTop: "1px solid #D8D8DC", paddingTop: 4 },
  reDec: { fontSize: 8.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 3, color: "#667085" },
  reDecOk: { color: "#171717" },
  reDecWait: { color: "#98A2B3" },
  reDecNo: { color: "#667085" },
  reNoApprovals: { fontSize: 11, color: "#98A2B3", paddingBottom: 5 },

  /* ready / success screen */
  success: { display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "24px 20px" },
  okCircle: { width: 60, height: 60, borderRadius: "50%", background: "#F0FDF4", color: "#16A34A", display: "grid", placeItems: "center", marginBottom: 16 },
  okTitle: { fontSize: 18, fontWeight: 700, color: c.ink, fontFamily: FONT },
  okDesc: { fontSize: 13, color: c.muted, margin: "4px 0 20px", fontFamily: FONT },
  fileChip: {
    display: "flex", alignItems: "center", gap: 12, width: "100%", maxWidth: 340, border: `1px solid ${c.line}`,
    borderRadius: 10, padding: "12px 14px", marginBottom: 20,
  },
  fileIco: { width: 38, height: 38, borderRadius: 8, background: "#FEF2F2", color: "#B91C1C", fontSize: 10, fontWeight: 800, display: "grid", placeItems: "center", flexShrink: 0 },
  fn: { fontSize: 13.5, fontWeight: 600, color: c.ink, fontFamily: FONT },
  fmeta: { fontSize: 11.5, color: c.muted2, fontFamily: FONT },
  downloadAgainBtn: {
    minWidth: 210, justifyContent: "center", height: 40, borderRadius: 8, background: c.accent, border: 0,
    color: c.ink, fontSize: 13.5, fontWeight: 700, fontFamily: FONT,
    "&:hover": { background: `${c.accent} !important`, opacity: 0.85 },
  },
  linklike: {
    border: 0, background: "none", color: c.accentHover, fontWeight: 600, fontSize: 12, cursor: "pointer",
    fontFamily: FONT, marginTop: 12,
  },
}));

// Shared Mantine classNames — mirrors #drawer-report .rdd / .rms / .rq-ctl from the mockup
const useFieldStyles = createStyles(() => ({
  label: { fontSize: 11.5, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: c.muted2, marginBottom: 6, fontFamily: FONT },
  required: { color: c.danger },
  input: {
    minHeight: 40, borderRadius: 8, border: `1px solid ${c.line}`, background: c.surface, fontSize: 13.5, color: c.ink,
    fontFamily: FONT,
    "&:focus, &:focus-within": { borderColor: c.accent },
  },
  value: { background: c.surfaceAlt, border: `1px solid ${c.line}`, borderRadius: 6, fontSize: 12.5, color: c.ink, fontFamily: FONT },
  dropdown: { borderRadius: 10, border: `1px solid ${c.line}`, boxShadow: "0 12px 30px rgba(16,24,40,.14)" },
  // Mirrors .rdd-opt: the option itself never highlights on selection —
  // only its trailing checkmark (.chk) does.
  item: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
    borderRadius: 7, fontSize: 13.5, color: c.ink, fontFamily: FONT,
    "&:hover": { background: c.surfaceAlt },
    "&::after": { content: '"✓"', opacity: 0, color: c.ink, fontWeight: 700 },
    "&[data-selected]::after": { opacity: 1 },
    "&[data-selected]": { background: "transparent", color: c.ink, fontWeight: 400 },
    "&[data-selected]:hover": { background: c.surfaceAlt },
  },
  description: { fontSize: 11.5, color: c.muted2, marginTop: 4, fontFamily: FONT },
}));

const useSegStyles = createStyles(() => ({
  root: { background: "transparent", padding: 0, gap: 8, width: "fit-content" },
  indicator: { display: "none" },
  control: { border: "none" },
  label: {
    border: `1px solid ${c.line}`, borderRadius: 8, padding: "8px 18px", fontSize: 13, fontWeight: 600, color: c.muted,
    fontFamily: FONT, background: c.surface,
    "&[data-active]": { background: c.ink, borderColor: c.ink, color: "#fff" },
    "&[data-active]:hover": { background: c.ink },
  },
}));

// `date` / `submittedAt` come back as ISO strings, not the mockup's pre-formatted labels.
const fmtDay = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : dayLabel(d);
};

/* ---- functional date-range calendar popover (visual port of mockup's rep-cal) ---- */
const DateRangeCalendar = ({ classes: s, value, onChange, onClose }) => {
  const [start, end] = value;
  const [view, setView] = useState(() => {
    const base = start || new Date();
    return { y: base.getFullYear(), m: base.getMonth() };
  });
  const presets = useMemo(buildPresets, []);

  const cells = useMemo(() => {
    const startDow = new Date(view.y, view.m, 1).getDay();
    const daysIn = new Date(view.y, view.m + 1, 0).getDate();
    const prevDays = new Date(view.y, view.m, 0).getDate();
    const out = [];
    for (let i = 0; i < startDow; i++) {
      const m = view.m === 0 ? 11 : view.m - 1;
      const y = view.m === 0 ? view.y - 1 : view.y;
      out.push({ date: new Date(y, m, prevDays - startDow + 1 + i), muted: true });
    }
    for (let d = 1; d <= daysIn; d++) out.push({ date: new Date(view.y, view.m, d), muted: false });
    let nd = 1;
    while (out.length < 42) {
      const m = view.m === 11 ? 0 : view.m + 1;
      const y = view.m === 11 ? view.y + 1 : view.y;
      out.push({ date: new Date(y, m, nd++), muted: true });
    }
    return out;
  }, [view]);

  const dayClass = (cellDate, muted) => {
    const n = dnum(cellDate);
    const cls = [s.calDay];
    if (muted) cls.push(s.calDayMuted);
    const sStart = start && n === dnum(start);
    const sEnd = end && n === dnum(end);
    const inRange = start && end && n > dnum(start) && n < dnum(end);
    if (inRange) cls.push(s.calDayInRange);
    if (sStart && sEnd) cls.push(s.calDayStartEnd);
    else if (sStart) cls.push(s.calDayStart);
    else if (sEnd) cls.push(s.calDayEnd);
    return cls.join(" ");
  };

  const pickDay = (date) => {
    if (!start || (start && end)) {
      onChange([date, null]);
    } else if (date < start) {
      onChange([date, start]);
    } else {
      onChange([start, date]);
    }
  };

  const applyPreset = (p) => {
    const [pStart, pEnd] = p.range();
    onChange([pStart, pEnd]);
    setView({ y: pStart.getFullYear(), m: pStart.getMonth() });
  };

  const isPresetActive = (p) => {
    if (!start || !end) return false;
    const [pStart, pEnd] = p.range();
    return dnum(start) === dnum(pStart) && dnum(end) === dnum(pEnd);
  };

  const navMonth = (dir) => {
    setView((v) => {
      let m = v.m + dir, y = v.y;
      if (m < 0) { m = 11; y -= 1; }
      if (m > 11) { m = 0; y += 1; }
      return { y, m };
    });
  };


  return (
    <Box className={s.cal} onClick={(e) => e.stopPropagation()}>
      <Box className={s.calTop}>
        <Box className={s.presets}>
          {presets.map((p) => (
            <button
              key={p.k}
              type="button"
              className={`${s.preset} ${isPresetActive(p) ? s.presetActive : ""}`}
              onClick={() => applyPreset(p)}
            >
              {p.label}
            </button>
          ))}
        </Box>
        <Box className={s.calMain}>
          <Box className={s.calHead}>
            <ActionIcon className={s.calNav} onClick={() => navMonth(-1)} aria-label="Previous month">
              <IconChevronLeft size={15} />
            </ActionIcon>
            <Text className={s.calTitle}>{CAL_MONTHS[view.m]} {view.y}</Text>
            <ActionIcon className={s.calNav} onClick={() => navMonth(1)} aria-label="Next month">
              <IconChevronRight size={15} />
            </ActionIcon>
          </Box>
          <Box className={s.calGrid}>
            {DOW.map((d) => <Box key={d} className={s.calDow}>{d}</Box>)}
            {cells.map(({ date, muted }, i) => (
              <button
                key={i}
                type="button"
                className={dayClass(date, muted)}
                onClick={() => pickDay(date)}
              >
                {date.getDate()}
              </button>
            ))}
          </Box>
        </Box>
      </Box>
      <Box className={s.calFoot}>
        <Text className={s.calRangeLbl}>
          {start && end
            ? <><strong>{dayLabel(start)}</strong> to <strong>{dayLabel(end)}</strong></>
            : start
              ? <><strong>{dayLabel(start)}</strong> — pick end date</>
              : "Pick a start date"}
        </Text>
        <button type="button" className={s.calDoneBtn} onClick={onClose}>Done</button>
      </Box>
    </Box>
  );
};

const fmtRange = ([start, end]) => {
  if (!start) return "Select dates";
  if (!end) return `${CAL_MABBR[start.getMonth()]} ${pad2(start.getDate())}, ${start.getFullYear()}`;
  return `${CAL_MABBR[start.getMonth()]} ${pad2(start.getDate())} – ${CAL_MABBR[end.getMonth()]} ${pad2(end.getDate())}, ${end.getFullYear()}`;
};

/* ---- multi-select field: label row + "Select all", control, then hint below (mockup's repMS) ---- */
const ReportMultiSelect = ({
  classes: s, fieldClasses, label, required, hint, span = 12, data, value, onChange,
}) => (
  <Box className={span === 6 ? s.span6 : s.span12}>
    <Box className={s.lblRow}>
      <Text component="span" className={s.msLabel}>
        {label}{required && <Text span className={s.req}> *</Text>}
      </Text>
      <button type="button" className={s.linkBtn} onClick={() => onChange([...data])}>Select all</button>
    </Box>
    <MultiSelect
      classNames={fieldClasses}
      placeholder="Select…"
      rightSection={<IconChevronDown size={15} color={c.muted2} />}
      data={data}
      value={value}
      onChange={onChange}
      searchable
    />
    {hint && <Box className={s.hint}>{hint}</Box>}
  </Box>
);

/* ---- per-request PDF entry block (visual port of mockup's srEntryBlock) ---- */
// `allPatients`/`allStaff` resolve a custom Client/Staff-type template
// field's raw fieldValues answer (an array of ids) back to names — the
// same directories the drawer already reads for its Scope filters, not a
// separate fetch.
const ServiceRequestEntry = ({ r, template, classes: s, includeApprovals = true, includeSignatures = true, allPatients = [], allStaff = [] }) => {
  const type = displayRef(r.template).toString().replace(" Request", "");
  const clients = (r.clients || []).filter(Boolean).map(clientRefName);
  const staffNames = (r.staff || []).filter(Boolean).map(displayRef);
  const clientLabel = clients.length > 1 ? `Clients (${clients.length})` : "Client(s)";
  const staffLabel = staffNames.length > 1 ? `Staff (${staffNames.length})` : "Staff";

  const F = (k, v) => (v ? (
    <Box className={s.reF} key={k}>
      <Box className={s.reFK}>{k}</Box>
      <Box className={s.reFV}>{v}</Box>
    </Box>
  ) : null);

  // A custom template's own Client/Staff-type field is the equivalent of
  // the built-in Client(s)/Staff rows below — showing both would put a
  // real answer right next to a misleading "None specified"/"Unassigned"
  // for the very same request. Facility works the same way, but has no
  // generic row here to begin with (it's already resolved in the header),
  // so its own raw field is just dropped rather than shown unresolved.
  const templateClientField = (template?.fields || []).find((f) => f.fieldType === "Client");
  const templateStaffField = (template?.fields || []).find((f) => f.fieldType === "Staff");

  // fieldValues is keyed by fieldId, so the template definition is what turns
  // it back into labelled rows — same resolution the detail drawer does.
  const templateFields = (template?.fields || [])
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .filter((f) => f.fieldType !== "Notifications" && f.fieldType !== "Facility")
    .map((f) => {
      const v = r.fieldValues?.[f.fieldId];
      if (f.fieldType === "Client") {
        const names = (Array.isArray(v) ? v : [v]).filter(Boolean)
          .map((id) => allPatients.find((p) => refId(p) === refId(id))?.name || null)
          .filter(Boolean);
        return F(f.label, names.length ? names.join(", ") : "None specified");
      }
      if (f.fieldType === "Staff") {
        const names = (Array.isArray(v) ? v : [v]).filter(Boolean)
          .map((id) => displayRef(allStaff.find((st) => refId(st) === refId(id))) || null)
          .filter((n) => n && n !== "—");
        return F(f.label, names.length ? names.join(", ") : "Unassigned");
      }
      return F(f.label, Array.isArray(v) ? v.join(", ") : v?.toString?.());
    });

  const fields = [
    F("Request Type", type),
    F("Status", STATUS_LABELS[r.status] || r.status),
    F("Priority", r.priority),
    F("Category", r.issueCategory ? displayRef(r.issueCategory) : null),
    templateClientField ? null : F(clientLabel, clients.length ? clients.join(", ") : "None specified"),
    templateStaffField ? null : F(staffLabel, staffNames.length ? staffNames.join(", ") : "Unassigned"),
    F("Shift", r.shift),
    F("Date", fmtDay(r.date)),
    F("Time", r.time),
    ...templateFields,
  ];

  // `r.approvals` only ever grows as a vote is actually cast — an approver
  // who hasn't voted yet has no entry in it at all, unlike the mockup's own
  // data (an object pre-seeded with every approver, decision: null). Build
  // that same full roster from the template's configured approver list —
  // same resolution the detail drawer's sigRows already does — so a
  // not-yet-decided approver still shows up as "Pending" instead of being
  // silently missing from the trail.
  const templateApproverIds = (template?.approvers || []).map(refId).filter(Boolean);
  const approvalRows = templateApproverIds.length
    ? templateApproverIds.map((id) => {
        const vote = (r.approvals || []).find((a) => refId(a.approver) === id);
        const person = vote?.approver || allStaff.find((st) => refId(st) === id) || null;
        return { id, decision: vote?.decision || null, person, vote };
      })
    : (r.approvals || []).map((a) => ({ id: refId(a.approver), decision: a.decision, person: a.approver, vote: a }));
  const decClass = (d) => (d === "approved" ? s.reDecOk : d === "rejected" ? s.reDecNo : s.reDecWait);
  const decText = (d) => (d === "approved" ? "Approved" : d === "rejected" ? "Rejected" : "Pending");

  return (
    <Box className={s.reEntry}>
      <Box className={s.reHead}>
        <Box className={s.reWho}>
          <Box className={s.reK}>{r.requestNumber || r.id}</Box>
          <Box className={s.reClient}>{r.title}</Box>
        </Box>
        <Box className={s.reWhen}>
          <Box className={s.reWhenD}>{fmtDay(r.date) || fmtDay(r.submittedAt) || "—"}</Box>
          <Box className={s.reWhenS}>{r.shift || "No shift"} · {displayRef(r.facility)}</Box>
        </Box>
      </Box>
      <Box className={s.reBody}>
        {fields}
        <Box className={`${s.reF} ${s.reFFull}`}>
          <Box className={s.reFK}>Description</Box>
          <Box className={`${s.reFV} ${s.reFVBoxed}`}>{r.description || "No description provided."}</Box>
        </Box>
      </Box>
      {includeApprovals && (
        <Box className={s.reFoot}>
          <Text className={s.reSiglbl}>Approval Trail ({approvalRows.length})</Text>
          {approvalRows.length ? approvalRows.map(({ id, decision, person, vote }, i) => {
            const who = person ? displayRef(person) : "Approver";
            // The real on-file signature, decrypted client-side — same
            // signatureSnapshot the detail drawer's Approval Signatures
            // card and sign-off modal already read, not a stand-in.
            const sigImage = decision === "approved"
              ? decrypt(vote?.signatureSnapshot?.signature) || decrypt(vote?.signatureSnapshot?.signatureImage)
              : null;
            const sigText = decision === "approved" ? vote?.signatureSnapshot?.signatureText : null;
            return (
              <Box className={s.reSigblk} key={id || i}>
                {includeSignatures && (
                  <Box className={s.reSig}>
                    {sigImage ? (
                      <img src={sigImage} alt="signature" className={s.reSigImg} />
                    ) : sigText ? (
                      sigText
                    ) : "—"}
                  </Box>
                )}
                <Box className={s.reSigname}>{who}</Box>
                <Box className={`${s.reDec} ${decClass(decision)}`}>{decText(decision)}</Box>
              </Box>
            );
          }) : <Text className={s.reNoApprovals}>No approvals configured for this request type.</Text>}
        </Box>
      )}
    </Box>
  );
};

const ServiceRequestReportDrawer = ({ opened, close }) => {
  const { classes } = useStyles();
  // Both steps are dense — a filter form and a page preview — so the drawer
  // takes the whole viewport on a phone rather than 640px of it.
  const isMobile = useMediaQuery("(max-width: 768px)", undefined, { getInitialValueInEffect: false });
  const { classes: field } = useFieldStyles();
  const { classes: seg } = useSegStyles();
  const { templates } = useSelector((state) => state.serviceRequests);
  const { profile } = useSelector((state) => state.profile);
  // Cover page's "Generated by" — the tenant's own Administrator account has
  // no role/type of its own, same "Owner" convention the detail drawer's
  // sign-off modal already uses.
  const generatedByName = profile?.user?.name
    || [profile?.user?.firstName, profile?.user?.lastName].filter(Boolean).join(" ") || "—";
  const generatedByRole = !profile?.user?.type
    ? "Owner"
    : (profile?.user?.role ? profile.user.role.charAt(0).toUpperCase() + profile.user.role.slice(1) : "");
  const generatedBy = `${generatedByName}${generatedByRole ? ` (${generatedByRole})` : ""} · ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} · ${new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
  // Scope filter options come from directories the app has already loaded
  // elsewhere (or will lazily load the first time some other screen needs
  // them) — read only, never fetched from here, so opening this drawer
  // never fires a request on its own; only clicking Generate does.
  const allFacilities = useSelector((state) => state.facility?.allFacilities?.facilities) || [];
  const allPatients = useSelector((state) => state.patient?.allPatients) || [];
  const allStaff = useSelector((state) => state.staff?.allStaff) || [];

  // Own copy of the report data: the store's `requests` is the backlog
  // view's paginated list and is empty while the board view is mounted, so
  // reading it here produced an empty report whenever the drawer was opened
  // from the board (or once the list had been narrowed by the page's own
  // search). Fetched only once Generate is clicked, not on drawer open.
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);

  const [facility, setFacility] = useState(ALL_FACILITIES);
  const [shift, setShift] = useState(ANY_SHIFT);
  const [clients, setClients] = useState([]);
  const [types, setTypes] = useState([]);
  const [staff, setStaff] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [calOpen, setCalOpen] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);
  const [reportTitle, setReportTitle] = useState("");
  const [groupBy, setGroupBy] = useState("Client");
  const [sortOrder, setSortOrder] = useState("Newest first");
  const [includeSignatures, setIncludeSignatures] = useState("On");
  const [includeApprovals, setIncludeApprovals] = useState("On");
  const [reportStep, setReportStep] = useState("config");
  const [downloading, setDownloading] = useState(false);

  // One entry per rendered `.pdfpage` node (cover + one per request) — reset
  // every render so a page removed by a filter change can't leave a stale,
  // now-detached node behind for the next capture.
  const pageRefs = useRef([]);
  pageRefs.current = [];
  const setPageRef = (i) => (el) => { pageRefs.current[i] = el; };

  const [startDate, endDate] = dateRange;

  // Reset to the un-fetched state each time the drawer reopens, so a stale
  // report from a previous open can't leak into a fresh one.
  useEffect(() => {
    if (!opened) return;
    setRows([]);
  }, [opened]);

  const facilities = useMemo(() => [...new Set(
    allFacilities.filter((f) => f?.active !== false && f?.deleted !== true).map((f) => f.name).filter(Boolean)
  )], [allFacilities]);
  const clientOptions = useMemo(() => [...new Set(
    allPatients.filter((p) => p?.active !== false && p?.deleted !== true).map((p) => p.name).filter(Boolean)
  )], [allPatients]);
  const typeOptions = useMemo(() => [...new Set(
    (templates || []).filter((t) => t.active !== false).map((t) => t.name).filter(Boolean)
  )], [templates]);
  const staffOptions = useMemo(() => [...new Set(
    allStaff.filter((s) => s?.active !== false && s?.deleted !== true).map((s) => displayRef(s)).filter(Boolean)
  )], [allStaff]);

  const templateFor = (r) => (r.template && typeof r.template === "object" && r.template.fields
    ? r.template
    : (templates || []).find((t) => t.id === refId(r.template)));

  const filtered = useMemo(() => rows.filter((r) => {
    if (facility !== ALL_FACILITIES && displayRef(r.facility) !== facility) return false;
    if (shift !== ANY_SHIFT && r.shift !== shift) return false;
    if (clients.length && !(r.clients || []).some((cl) => clients.includes(clientRefName(cl)))) return false;
    if (types.length && !types.includes(displayRef(r.template))) return false;
    if (staff.length && !(r.staff || []).some((s) => staff.includes(displayRef(s)))) return false;
    // endDate lands on 00:00 of the chosen day, so comparing raw timestamps
    // dropped everything created later that same day — including "today"
    // on every preset. Compare on day boundaries instead.
    const created = r.createdAt ? new Date(r.createdAt) : null;
    if (created && !Number.isNaN(created.getTime())) {
      if (startDate && dnum(created) < dnum(startDate)) return false;
      if (endDate && dnum(created) > dnum(endDate)) return false;
    }
    return true;
  }), [rows, facility, shift, clients, types, staff, startDate, endDate]);

  // Advanced options' Group by/Sort order: clusters entries by the chosen
  // key (each still gets its own page — grouping shows up as adjacency,
  // called out via groupLabel below, not a separate section page), then
  // orders each cluster by date per Sort order.
  const groupKeyOf = (r) => {
    if (groupBy === "Request type") return displayRef(r.template);
    if (groupBy === "Date") return fmtDay(r.date) || fmtDay(r.submittedAt) || "Undated";
    const names = (r.clients || []).filter(Boolean).map(clientRefName);
    return names[0] || "Ungrouped";
  };
  const sortedFiltered = useMemo(() => {
    const dateOf = (r) => new Date(r.date || r.submittedAt || 0).getTime() || 0;
    const dir = sortOrder === "Oldest first" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const ga = groupKeyOf(a);
      const gb = groupKeyOf(b);
      if (ga !== gb) return ga < gb ? -1 : 1;
      return (dateOf(a) - dateOf(b)) * dir;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, groupBy, sortOrder]);

  // Captures the exact same styled pages the preview shows — one canvas per
  // `.pdfpage` node — rather than re-describing the report as a second,
  // separate set of jsPDF text/line calls that would drift from whatever
  // ServiceRequestEntry actually renders. Same html2canvas+jsPDF pattern
  // ReportViewModal's MAR export already uses.
  const downloadReportPdf = async () => {
    const nodes = pageRefs.current.filter(Boolean);
    if (typeof window === "undefined" || !nodes.length) return;
    setDownloading(true);
    // The preview page is fluid (`width: 100%, maxWidth: 600`) so it fits a phone
    // — but the capture is what becomes the PDF, and each canvas is stretched to
    // fill a letter page. Captured at a phone's ~330px the aspect no longer
    // matches letter and every page comes out horizontally stretched. Pinning the
    // desktop geometry (600px wide, its full padding) for the duration of the
    // capture makes a phone-generated PDF byte-for-byte the same document as a
    // desktop one. Restored in `finally`, so a mid-capture throw can't leave the
    // preview stuck at a width the viewport can't show.
    const pinned = nodes.map((n) => ({ node: n, width: n.style.width, padding: n.style.padding }));
    pinned.forEach(({ node }) => {
      node.style.width = "600px";
      node.style.padding = "40px 44px";
    });
    try {
      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      for (let i = 0; i < nodes.length; i++) {
        const canvas = await html2canvas(nodes[i], {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
        });
        const imgData = canvas.toDataURL("image/png");
        if (i > 0) doc.addPage();
        doc.addImage(imgData, "PNG", 0, 0, pageWidth, pageHeight);
      }
      const fileTitle = (reportTitle || "service-requests-report").trim().replace(/\s+/g, "-").toLowerCase();
      doc.save(`${fileTitle}.pdf`);
    } finally {
      pinned.forEach(({ node, width, padding }) => {
        node.style.width = width;
        node.style.padding = padding;
      });
      setDownloading(false);
    }
  };

  const downloadAll = () => {
    downloadReportPdf();
  };

  const goToPreview = () => setReportStep("preview");
  const goToReady = () => setReportStep("ready");
  const backToConfig = () => setReportStep("config");

  // The two fields marked with a red asterisk (Request type(s), Date range)
  // were visually "required" but nothing actually stopped Generate when
  // they were left empty.
  const validateScope = () => {
    if (!types.length) {
      showError("Select at least one request type before generating a report.");
      return false;
    }
    if (!startDate || !endDate) {
      showError("Pick a date range before generating a report.");
      return false;
    }
    return true;
  };

  // The only place this drawer ever calls the report API — a deliberate
  // choice so opening the drawer, or changing any filter, never fires a
  // request on its own.
  const generate = async () => {
    if (!validateScope()) return;
    setRowsLoading(true);
    try {
      const data = await fetchServiceRequestsForReport();
      setRows(data);
      goToPreview();
    } finally {
      setRowsLoading(false);
    }
  };

  const downloadAndReady = async () => {
    await downloadReportPdf();
    goToReady();
  };

  const reset = () => {
    setFacility(ALL_FACILITIES); setShift(ANY_SHIFT); setClients([]); setTypes([]); setStaff([]);
    setDateRange([null, null]); setCalOpen(false); setAdvOpen(false); setReportTitle("");
    setGroupBy("Client"); setSortOrder("Newest first"); setIncludeSignatures("On"); setIncludeApprovals("On");
    setReportStep("config");
  };

  const totalPages = filtered.length + 1;
  const rangeLabel = fmtRange(dateRange);

  // fieldValues is keyed by fieldId; the template definition supplies the labels.
  return (
    <Drawer
      opened={opened}
      onClose={() => { close(); reset(); }}
      position="right"
      size={isMobile ? "100%" : reportStep === "preview" ? "720px" : "640px"}
      withCloseButton={false}
      padding={0}
      classNames={{ content: `${classes.drawerContent} ${reportStep === "preview" ? classes.drawerCentered : ""}` }}
      overlayProps={{ opacity: 0.55, blur: 2, color: "#000" }}
      zIndex={1000}
    >
      <Box className={classes.root}>
        <Box className={`${classes.header} ${classes.printHide}`}>
          <Box className={classes.headerMin}>
            <Text className={classes.title}>
              {reportStep === "config" ? "Create Report" : reportStep === "preview" ? "Report Preview" : "Report Ready"}
            </Text>
            {reportStep === "preview" && (
              <Box className={classes.headerSub}>
                <span>Service Requests Report · {totalPages} pages</span>
              </Box>
            )}
          </Box>
          <ActionIcon className={classes.closeBtn} onClick={close} aria-label="Close"><IconX size={15} /></ActionIcon>
        </Box>

        {reportStep === "config" && (
          <>
            <Box className={classes.body}>
              {rowsLoading && (
                <Box style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 2px" }}>
                  <Loader size="xs" color="yellow" />
                  <Text className={classes.hint} style={{ marginTop: 0 }}>Loading requests…</Text>
                </Box>
              )}
              {/* Scope */}
              <Box className={classes.card}>
                <Box className={classes.cardHead}><IconUsers size={15} />Scope</Box>
                <Box className={classes.grid}>
                  <Select
                    label="Facility"
                    className={classes.span12}
                    classNames={field}
                    rightSection={<IconChevronDown size={15} color={c.muted2} />}
                    data={[ALL_FACILITIES, ...facilities]}
                    value={facility}
                    onChange={(v) => setFacility(v || ALL_FACILITIES)}
                  />
                  <ReportMultiSelect
                    classes={classes}
                    fieldClasses={field}
                    label="Client(s)"
                    hint="Leave empty to include every client at the selected facility."
                    data={clientOptions}
                    value={clients}
                    onChange={setClients}
                  />
                  <ReportMultiSelect
                    classes={classes}
                    fieldClasses={field}
                    label="Request type(s)"
                    required
                    data={typeOptions}
                    value={types}
                    onChange={setTypes}
                  />
                  <ReportMultiSelect
                    classes={classes}
                    fieldClasses={field}
                    label="Staff"
                    span={6}
                    data={staffOptions}
                    value={staff}
                    onChange={setStaff}
                  />
                  <Box className={classes.span6}>
                    {/* Matches ReportMultiSelect's own label markup exactly (rather
                        than Select's built-in `label` prop) so "Staff" and "Shift"
                        — side by side in the same row — sit on identical label-row
                        height instead of drifting apart under Mantine's own
                        Input.Wrapper spacing. */}
                    <Box className={classes.lblRow}>
                      <Text component="span" className={classes.msLabel}>Shift</Text>
                    </Box>
                    <Select
                      classNames={field}
                      rightSection={<IconChevronDown size={15} color={c.muted2} />}
                      data={[ANY_SHIFT, ...SHIFTS]}
                      value={shift}
                      onChange={(v) => setShift(v || ANY_SHIFT)}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Period */}
              <Box className={classes.card}>
                <Box className={classes.cardHead}><IconCalendar size={15} />Period</Box>
                <Box className={classes.dateRangeWrap}>
                  <Text className={classes.dateRangeLabel}>Date range <Text span className={field.required}>*</Text></Text>
                  <button
                    type="button"
                    className={`${classes.dateRangeBtn} ${calOpen ? classes.dateRangeBtnOpen : ""}`}
                    onClick={() => setCalOpen((v) => !v)}
                  >
                    <span>{rangeLabel}</span>
                    <IconCalendar size={16} />
                  </button>
                  {calOpen && (
                    <DateRangeCalendar
                      classes={classes}
                      value={dateRange}
                      onChange={setDateRange}
                      onClose={() => setCalOpen(false)}
                    />
                  )}
                </Box>
              </Box>

              {/* Advanced toggle */}
              <Button
                variant="default"
                className={classes.advBtn}
                leftIcon={<IconSettings size={15} />}
                rightIcon={(
                  <IconChevronDown size={15} className={advOpen ? classes.advCaretUp : classes.advCaretDown} />
                )}
                onClick={() => setAdvOpen((v) => !v)}
              >
                Advanced
              </Button>

              {/* Advanced options */}
              {advOpen && (
                <Box className={classes.card}>
                  <Box className={classes.cardHeadRow}>
                    <Box className={classes.cardHead} style={{ marginBottom: 0 }}><IconFileText size={15} />Advanced options</Box>
                    <ActionIcon className={classes.advCloseBtn} aria-label="Close advanced options" onClick={() => setAdvOpen(false)}>
                      <IconX size={16} />
                    </ActionIcon>
                  </Box>
                  <Box className={classes.grid}>
                    <TextInput
                      label={<>Report title <Text span className={classes.optTag}>(optional)</Text></>}
                      className={classes.span12}
                      classNames={field}
                      placeholder="e.g. Sunrise House, July Maintenance"
                      value={reportTitle}
                      onChange={(e) => setReportTitle(e.currentTarget.value)}
                    />
                    <Select
                      label="Group by"
                      className={classes.span6}
                      classNames={field}
                      rightSection={<IconChevronDown size={15} color={c.muted2} />}
                      data={["Client", "Request type", "Date"]}
                      value={groupBy}
                      onChange={setGroupBy}
                    />
                    <Select
                      label="Sort order"
                      className={classes.span6}
                      classNames={field}
                      rightSection={<IconChevronDown size={15} color={c.muted2} />}
                      data={["Newest first", "Oldest first"]}
                      value={sortOrder}
                      onChange={setSortOrder}
                    />
                    <Box className={classes.span6}>
                      <Text className={classes.segLabel}>Include staff signatures</Text>
                      <SegmentedControl classNames={seg} data={["Off", "On"]} value={includeSignatures} onChange={setIncludeSignatures} />
                    </Box>
                    <Box className={classes.span6}>
                      <Text className={classes.segLabel}>Include approval trail</Text>
                      <SegmentedControl classNames={seg} data={["Off", "On"]} value={includeApprovals} onChange={setIncludeApprovals} />
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            <Box className={`${classes.footer} ${classes.printHide}`}>
              <Button variant="default" className={classes.cancelBtn} onClick={close}>Cancel</Button>
              <Button
                variant="filled"
                leftIcon={<IconFileText size={16} />}
                onClick={generate}
                className={classes.primaryBtn}
                loading={rowsLoading}
                disabled={rowsLoading}
              >
                {rowsLoading ? "Loading…" : "Generate report"}
              </Button>
            </Box>
          </>
        )}

        {(reportStep === "preview" || reportStep === "ready") && (
          <Box
            className={`${classes.body} ${classes.previewStage} ${classes.printShell} ${reportStep === "ready" ? classes.previewOffscreen : ""}`}
          >
              {/* cover page */}
              <Box className={classes.pdfpage} ref={setPageRef(0)}>
                <Box className={classes.repPage}>
                  <Box className={classes.pdfLh}>
                    <Box className={classes.pdfBrand}>
                      <Box className={classes.pdfLogo}>
                        <img src="/assets/images/logoIcon.png" alt="Ogiso logo" className={classes.pdfLogoImg} />
                      </Box>
                      <Box className={classes.pdfOrg}>Ogiso Care</Box>
                    </Box>
                    <Box className={classes.pdfRt}>Generated {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}<br />Confidential</Box>
                  </Box>
                  <Box className={classes.rcMid}>
                    <Text className={classes.pdfTitle} style={{ fontSize: 28 }}>{reportTitle || "Service Requests Report"}</Text>
                    <Box className={classes.pdfSub2} style={{ marginBottom: 26 }}>{rangeLabel}</Box>
                    <Box className={classes.rcGrid}>
                      <Box className={classes.rcRow}><span className={classes.rcK}>Facility</span><span className={classes.rcV}>{facility || "All facilities"}</span></Box>
                      <Box className={classes.rcRow}><span className={classes.rcK}>Report period</span><span className={classes.rcV}>{rangeLabel}</span></Box>
                      <Box className={classes.rcRow}><span className={classes.rcK}>Total requests</span><span className={classes.rcV}>{filtered.length}</span></Box>
                      <Box className={classes.rcRow}><span className={classes.rcK}>Request types</span><span className={classes.rcV}>{[...new Set(filtered.map((r) => displayRef(r.template)))].join(", ")}</span></Box>
                      <Box className={classes.rcRow}><span className={classes.rcK}>Clients referenced</span><span className={classes.rcV}>{[...new Set(filtered.flatMap((r) => (r.clients || []).map(clientRefName)))].length}</span></Box>
                      <Box className={classes.rcRow}><span className={classes.rcK}>Staff involved</span><span className={classes.rcV}>{[...new Set(filtered.flatMap((r) => (r.staff || []).map(displayRef)))].length}</span></Box>
                      <Box className={classes.rcRow}><span className={classes.rcK}>Approved</span><span className={classes.rcV}>{filtered.filter((r) => r.status === "approved").length}</span></Box>
                      <Box className={classes.rcRow}><span className={classes.rcK}>Generated by</span><span className={classes.rcV}>{generatedBy}</span></Box>
                    </Box>
                  </Box>
                  <Box className={classes.pdfFoot}><span>Ogiso Care · Confidential service record</span><span>Page 1 of {totalPages}</span></Box>
                </Box>
              </Box>

              {/* per-request entry pages */}
              {sortedFiltered.map((r, i) => (
                <Box className={classes.pdfpage} key={r.id || i} ref={setPageRef(i + 1)}>
                  <Box className={classes.repPage}>
                    <Box className={classes.pdfLh}>
                      <Box className={classes.pdfBrand}>
                        <Box className={classes.pdfLogo}>
                          <img src="/assets/images/logoIcon.png" alt="Ogiso logo" className={classes.pdfLogoImg} />
                        </Box>
                        <Box className={classes.pdfOrg}>Ogiso Care</Box>
                      </Box>
                      <Box className={classes.pdfRt}>Generated {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}<br />Confidential</Box>
                    </Box>
                    {(i === 0 || groupKeyOf(r) !== groupKeyOf(sortedFiltered[i - 1])) && (
                      <Text className={classes.groupLabel}>{groupBy} · {groupKeyOf(r)}</Text>
                    )}
                    <Box className={classes.reList}>
                      <ServiceRequestEntry
                        r={r}
                        template={templateFor(r)}
                        classes={classes}
                        includeApprovals={includeApprovals === "On"}
                        includeSignatures={includeSignatures === "On"}
                        allPatients={allPatients}
                        allStaff={allStaff}
                      />
                    </Box>
                    <Box className={classes.pdfFoot}><span>Ogiso Care · Confidential service record</span><span>Page {i + 2} of {totalPages}</span></Box>
                  </Box>
                </Box>
              ))}
          </Box>
        )}

        {reportStep === "preview" && (
          <Box className={`${classes.footer} ${classes.printHide}`}>
            <Button variant="default" className={classes.cancelBtn} onClick={backToConfig} disabled={downloading}>Back</Button>
            <Button
              variant="filled"
              leftIcon={<IconDownload size={15} />}
              onClick={downloadAndReady}
              className={classes.primaryBtn}
              loading={downloading}
            >
              {downloading ? "Preparing PDF…" : "Download PDF"}
            </Button>
          </Box>
        )}

        {reportStep === "ready" && (
          <>
            <Box className={classes.body}>
              <Box className={classes.success}>
                <Box className={classes.okCircle}><IconCheck size={30} stroke={2.4} /></Box>
                <Text className={classes.okTitle}>Report ready</Text>
                <Text className={classes.okDesc}>{filtered.length} requests · {rangeLabel}</Text>
                <Box className={classes.fileChip}>
                  <Box className={classes.fileIco}>PDF</Box>
                  <Box style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                    <Box className={classes.fn}>Service Requests Report</Box>
                    <Box className={classes.fmeta}>PDF document · 0.6 MB</Box>
                  </Box>
                </Box>
                <Button
                  variant="filled"
                  leftIcon={<IconDownload size={15} />}
                  onClick={downloadAll}
                  className={classes.downloadAgainBtn}
                  loading={downloading}
                >
                  {downloading ? "Preparing PDF…" : "Download again"}
                </Button>
                <button type="button" className={classes.linklike} onClick={backToConfig}>
                  Create another report
                </button>
              </Box>
            </Box>
            <Box className={`${classes.footer} ${classes.printHide}`}>
              <Button variant="default" className={classes.cancelBtn} onClick={close}>Close</Button>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default ServiceRequestReportDrawer;
